import {
  EMPIEZA_POR,
  countryPreset,
  esPaisConocido,
  prefijoDe,
  type EmpiezaPor,
  type OnboardingGoal,
} from '@alexiarest/shared';

/**
 * Alta con Alexia (13 oct 2026; después del chat, 15 oct 2026): en la landing
 * Alexia pregunta lo justo (nombre, restaurante, país, apps de delivery y por
 * dónde empieza). Las respuestas viajan a la app en el FRAGMENTO de la URL
 * (`app.alexiarest.com/empezar#alta=<base64url>`): el fragmento no llega a
 * ningún servidor ni a los logs de CloudFront, y se borra de la barra en
 * cuanto se lee. Se guardan aquí hasta crear la cuenta (LoginPage →
 * AltaCuenta) y el restaurante nace con todo aplicado (OnboardingPage →
 * AltaEnMarcha): sin cuestionario, lo que falta queda en «Para empezar».
 *
 * Contrato con la landing (campos de `#alta=`):
 *   persona, restaurante, pais (ISO-2), delivery (nombres de apps),
 *   empieza ('plato' | 'factura' | 'numeros'; también se acepta
 *   empiezaPor con 'ventas'), plato (texto, opcional),
 *   moneda (ISO-4217, solo si la preguntó porque no conocíamos el país),
 *   newsletter, acceso ('correo' | 'google').
 *   `objetivo` ('costs' | 'numbers') sigue aceptándose de la landing de antes.
 */

export const TPV_ALTA = ['loyverse', 'square', 'revo', 'otro', 'ninguno'] as const;
export type TpvAlta = (typeof TPV_ALTA)[number];

export interface AltaConAlexia {
  /** Cómo se llama quien se da de alta (solo para hablarle por su nombre). */
  persona?: string;
  restaurante: string;
  /** ISO 3166-1 alfa-2. La moneda sale del país (o de `moneda`). */
  pais: string;
  /** Moneda elegida en el chat cuando el país no la daba sola. */
  moneda?: string;
  objetivo: OnboardingGoal;
  /** Lo que eligió para empezar: se abre en el lateral al entrar. */
  empiezaPor: EmpiezaPor;
  /** Plataformas de delivery marcadas (nombres de la lista de la landing). */
  delivery: string[];
  tpv?: TpvAlta;
  /** Ventas netas de un mes normal (landing anterior; ya no se usa). */
  ventasMes?: number;
  /** El plato estrella tal cual lo contó: se le pasa a Alexia al dictar. */
  plato?: string;
  whatsapp?: string;
  newsletter: boolean;
  /** Cómo quiso crear la cuenta en el chat (hoy solo correo: Google queda fuera). */
  acceso?: 'google' | 'correo';
}

const KEY = 'alexia:alta-con-alexia';
const TTL_MS = 7 * 86_400_000;

const texto = (v: unknown, max: number): string | undefined => {
  if (typeof v !== 'string') return undefined;
  const limpio = v.replace(/[\u0000-\u001f]/g, ' ').trim().slice(0, max);
  return limpio || undefined;
};

const esEmpiezaPor = (v: unknown): v is EmpiezaPor => (EMPIEZA_POR as readonly unknown[]).includes(v);

/** Valida lo que llega de fuera (la URL es editable): nada que no se entienda pasa. */
export function validarAlta(raw: unknown): AltaConAlexia | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const restaurante = texto(r.restaurante, 80);
  const pais = typeof r.pais === 'string' ? r.pais.trim().toUpperCase() : '';
  const objetivoDado = r.objetivo === 'costs' || r.objetivo === 'numbers' ? r.objetivo : null;
  // La landing nueva (15 oct 2026) manda `empieza: 'plato' | 'factura' | 'numeros'`.
  const empiezaCrudo = r.empiezaPor ?? (r.empieza === 'numeros' ? 'ventas' : r.empieza);
  const empiezaDado = esEmpiezaPor(empiezaCrudo) ? empiezaCrudo : null;
  if (!restaurante || !/^[A-Z]{2}$/.test(pais) || !esPaisConocido(pais) || (!objetivoDado && !empiezaDado)) return null;
  const empiezaPor: EmpiezaPor = empiezaDado ?? (objetivoDado === 'numbers' ? 'ventas' : 'plato');
  const objetivo: OnboardingGoal = objetivoDado ?? (empiezaPor === 'plato' ? 'costs' : 'numbers');
  const delivery = Array.isArray(r.delivery)
    ? [...new Set(r.delivery.map((d) => texto(d, 40)).filter((d): d is string => Boolean(d)))].slice(0, 10)
    : [];
  const tpv = (TPV_ALTA as readonly string[]).includes(r.tpv as string) ? (r.tpv as TpvAlta) : undefined;
  const ventas = typeof r.ventasMes === 'number' && Number.isFinite(r.ventasMes) && r.ventasMes > 0 && r.ventasMes < 1e11
    ? Math.round(r.ventasMes)
    : undefined;
  const whatsapp = texto(r.whatsapp, 24);
  const moneda = typeof r.moneda === 'string' && /^[A-Za-z]{3}$/.test(r.moneda.trim()) ? r.moneda.trim().toUpperCase() : undefined;
  return {
    persona: texto(r.persona, 60),
    restaurante,
    pais,
    moneda,
    objetivo,
    empiezaPor,
    delivery,
    tpv,
    ventasMes: ventas,
    plato: texto(r.plato, 1200),
    whatsapp: whatsapp && /^\+?[\d\s().-]{6,24}$/.test(whatsapp) ? whatsapp : undefined,
    newsletter: r.newsletter === true,
    acceso: r.acceso === 'google' || r.acceso === 'correo' ? r.acceso : undefined,
  };
}

function base64UrlADatos(valor: string): unknown {
  const b64 = valor.replace(/-/g, '+').replace(/_/g, '/');
  const binario = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
  const bytes = Uint8Array.from(binario, (c) => c.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
}

/** Lee `#alta=` de una URL. Exportada para los tests. */
export function altaDelFragmento(hash: string): AltaConAlexia | null {
  const valor = new URLSearchParams(hash.replace(/^#/, '')).get('alta');
  if (!valor || valor.length > 8000) return null;
  try { return validarAlta(base64UrlADatos(valor)); } catch { return null; }
}

/**
 * Al arrancar la app, antes del login: guarda lo que trae el fragmento y lo
 * quita de la barra de direcciones (nombre y teléfono no deben quedarse ahí).
 */
export function recordarAltaConAlexia(): void {
  if (!window.location.hash.includes('alta=')) return;
  const alta = altaDelFragmento(window.location.hash);
  window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
  if (!alta) return;
  guardarAlta(alta);
}

export function guardarAlta(alta: AltaConAlexia): void {
  try { localStorage.setItem(KEY, JSON.stringify({ at: Date.now(), alta })); } catch { /* sin storage: alta normal */ }
}

export function altaPendiente(): AltaConAlexia | null {
  try {
    const guardada = JSON.parse(localStorage.getItem(KEY) ?? 'null') as { at?: number; alta?: unknown } | null;
    if (!guardada || typeof guardada.at !== 'number' || Date.now() - guardada.at > TTL_MS) return null;
    return validarAlta(guardada.alta);
  } catch { return null; }
}

export function olvidarAlta(): void {
  try { localStorage.removeItem(KEY); } catch { /* nada que olvidar */ }
}

/*
 * El plato estrella contado en el chat espera al lateral de «dictar el
 * plato»: se guarda aparte para que el alta pueda olvidarse ya.
 */
const KEY_PLATO = 'alexia:plato-contado';
export function guardarPlatoContado(plato: string): void {
  try { localStorage.setItem(KEY_PLATO, plato); } catch { /* lo contará de nuevo */ }
}
export function platoContado(): string | null {
  try { return localStorage.getItem(KEY_PLATO); } catch { return null; }
}
export function olvidarPlatoContado(): void {
  try { localStorage.removeItem(KEY_PLATO); } catch { /* nada */ }
}

/* ------------------------------------------------------------------ */
/* Lo que se aplica a la cuenta recién creada                           */
/* ------------------------------------------------------------------ */

/** La API exige el prefijo: en el chat casi nadie lo escribe, se pone el del país. */
function conPrefijo(numero: string | undefined, pais: string): string | undefined {
  if (!numero) return undefined;
  if (numero.startsWith('+')) return numero;
  const prefijo = prefijoDe(pais);
  return prefijo ? `${prefijo} ${numero.replace(/^0+/, '')}` : undefined;
}

/**
 * Lo que viaja a POST /v1/onboarding. La API pone el resto según el país
 * (impuesto de las ventas, medidas) y crea las apps con su comisión habitual
 * marcada para confirmar.
 */
export function datosDeAlta(alta: AltaConAlexia, newsletter = alta.newsletter) {
  return {
    onboardingGoal: alta.objetivo,
    name: alta.restaurante,
    country: alta.pais,
    currency: alta.moneda ?? countryPreset(alta.pais).currency,
    whatsapp: conPrefijo(alta.whatsapp, alta.pais),
    newsletter,
    persona: alta.persona,
    empiezaPor: alta.empiezaPor,
    apps: alta.delivery,
    // Solo para el aviso interno de alta (no se guardan en la cuenta).
    tpv: alta.tpv,
    plato: alta.plato,
  };
}

/** «Empiezas por»: el texto del resumen de AltaCuenta. */
export const EMPIEZA_POR_TEXTO: Record<EmpiezaPor, string> = {
  plato: 'Tu plato estrella',
  factura: 'Subir una factura',
  ventas: 'Tus números del mes',
};
